<template>
  <Transition name="loading-fade">
    <div
      v-if="showOverlay"
      class="global-loading-overlay"
      :class="{ 'loading-blocking': blocking }"
      :style="{ zIndex: overlayZIndex }"
    >
      <div class="loading-content">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <span class="ms-2 fw-semibold loading-text">Aguarde...</span>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useUiStore } from '@/stores/ui'

defineProps({
  blocking: {
    type: Boolean,
    default: true
  }
})

const uiStore = useUiStore()
const overlayZIndex = ref(9999)
const showOverlay = ref(false)

let showTimer = null
let minDisplayTimer = null
let safetyTimer = null

const LIMIAR_EXIBICAO = 500 // ms: só exibe se o loading durar mais que isso
const TEMPO_MINIMO = 300 // ms: tempo mínimo visível
const SAFETY_TIMEOUT = 30000 // ms: auto-esconde após 30s (safety net)

watch(() => uiStore.apiCallCount, (count) => {
  // Se o loading começou (count > 0)
  if (count > 0) {
    // Cancela qualquer hide pendente
    if (minDisplayTimer) {
      clearTimeout(minDisplayTimer)
      minDisplayTimer = null
    }

    // Se o overlay já está visível, não faz nada
    if (showOverlay.value) return

    // Agenda exibição com debounce
    if (!showTimer) {
      showTimer = setTimeout(() => {
        showTimer = null

        // Z-index dinâmico
        const todos = document.querySelectorAll('*')
        let maxZ = 0
        for (const el of todos) {
          if (el.classList && el.classList.contains('notification-badge')) continue
          const z = parseInt(window.getComputedStyle(el).zIndex, 10)
          if (!isNaN(z) && z > maxZ) maxZ = z
        }
        overlayZIndex.value = maxZ + 1
        showOverlay.value = true

        // Safety net: se o loading ficar > 30s, esconde automaticamente
        if (safetyTimer) clearTimeout(safetyTimer)
        safetyTimer = setTimeout(() => {
          safetyTimer = null
          if (showOverlay.value) {
            showOverlay.value = false
          }
        }, SAFETY_TIMEOUT)
      }, LIMIAR_EXIBICAO)
    }
    return
  }

  // count === 0: loading terminou
  // Cancela show pendente
  if (showTimer) {
    clearTimeout(showTimer)
    showTimer = null
  }

  // Se o overlay não está visível, nada a fazer
  if (!showOverlay.value) return

  // Estava visível: garante tempo mínimo antes de esconder
  if (!minDisplayTimer) {
    minDisplayTimer = setTimeout(() => {
      minDisplayTimer = null
      showOverlay.value = false
      if (safetyTimer) {
        clearTimeout(safetyTimer)
        safetyTimer = null
      }
    }, TEMPO_MINIMO)
  }
}, { immediate: true })
</script>

<style scoped>
.global-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-blocking {
  pointer-events: all;
}

.loading-content {
  display: flex;
  align-items: center;
  background: white;
  padding: 12px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.loading-text {
  color: #0d6efd;
}

/* Transição suave de fade */
.loading-fade-enter-active,
.loading-fade-leave-active {
  transition: opacity 0.2s ease;
}

.loading-fade-enter-from,
.loading-fade-leave-to {
  opacity: 0;
}

/* ===== Dark Mode ===== */
[data-bs-theme="dark"] .global-loading-overlay {
  background: rgba(0, 0, 0, 0.6);
}

[data-bs-theme="dark"] .loading-content {
  background: var(--bs-tertiary-bg);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}

[data-bs-theme="dark"] .loading-text {
  color: var(--bs-info);
}
</style>
